import { useContext, useEffect, useMemo, useReducer, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHvSubstationCreation } from "../data/Assets/useHvSubstationCreation";

import { Stack, Typography } from "@mui/material";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Col, Container, Row } from "react-bootstrap";
import { Card } from "../shared/Card";
import { FormContainer, FormItem } from "../shared/FormLayout/FormContainer";
import { MessageToast, ToastMode } from "../shared/MessageToast";
import { ProgressButton } from "../shared/ProgressButton";

import { Address } from "../shared/interfaces/Address";
import { HvSubstation, HvSubstation2Submission } from "../shared/interfaces/HvSubstation";
import { TabView } from "../shared/TabView";
import { SwitchgearCreateDialog } from "./SwitchgearCreateDialog";
import { SwitchgearDetails } from "./SwitchgearDetails";

import { InputTextarea } from "primereact/inputtextarea";
import { CompanyDropdown } from "../CompanyDropdown";
import { AccessControlContext } from "../contexts/AccessControlContext";
import { useAlternativeRegionNames } from "../data/Settings/useAlternativeRegionNames";
import { useCompaniesByCustomerId } from "../data/useCompaniesByCustomerId";
import { CancelButton } from "../shared/CancelButton";
import { SurveyableAsset } from "../shared/interfaces/SurveyableAsset";
import { ValidationMessage } from "../shared/ValidationMessage";
import { SiteDropdown } from "../SiteDropdown";
import "./coordinateInput.css";
import { SwitchgearLabel } from "./SwitchgearLabel";
import { AuthContext } from "../contexts/AuthContext";

interface Props {
  open?: boolean;
  onClose: () => void;
  onHvSubstationCreated: (substationId: number, substationName: string) => void;
  customerId: number;
  existingSubstations: HvSubstation[] | undefined;
  refreshSubstationData: () => void;
  prepopulatedHvSubstationValues?: Partial<HvSubstation>;
}

export function HvSubstationCreate(props: Props) {
  const { t } = useTranslation();
  const formValidation = useForm({ mode: "onBlur" });

  const { onHvSubstationCreated, customerId } = props;

  const accessControlContext = useContext(AccessControlContext);
  const userCanAddHvSubstation = accessControlContext.canI("add", "hvSubstations", customerId);

  const authContext = useContext(AuthContext);
  const userCanAddHvSubstationAtCustomerLevel = authContext.canI("add", "hvSubstations", customerId);

  const [showErrorToast, setShowErrorToast] = useState(false);

  const [newHvSubstation, setNewHvSubstation] = useState<Partial<HvSubstation>>(
    props.prepopulatedHvSubstationValues || {},
  );

  const [switchgears, setSwitchgears] = useState<SurveyableAsset[]>(
    props.prepopulatedHvSubstationValues?.switchGears || [],
  );
  const [switchgearDialogVisible, setSwitchgearDialogVisible] = useState<boolean>(false);

  const [newAddress, setNewAddress] = useReducer((address: Partial<Address>, updatedAddress: Partial<Address>) => {
    return { ...address, ...updatedAddress };
  }, {});

  const hasAddressDetails = useMemo(() => {
    return Object.entries(newAddress).some(([key, value]) => {
      if (key === "name") {
        return false;
      }

      return value != null && value.toString().trim() !== "";
    });
  }, [newAddress]);

  const isAddressNameMissing = hasAddressDetails && !newAddress.name?.toString().trim();

  const onSubmit = formValidation.handleSubmit(() => {
    props.refreshSubstationData();
    if (userCanAddHvSubstation) {
      setShowErrorToast(false);

      if (isAddressNameMissing) {
        return;
      }

      // Check if the address has been populated
      if (Object.keys(newAddress).length > 0) {
        newHvSubstation.address = newAddress;
      }

      switchgears.forEach((switchgear) => {
        const fieldsToCheck = [
          "switchGearManufacturer",
          "switchGearType",
          "installDate",
          "insulation",
          "operatingVoltage",
          "ratedVoltage",
        ];

        fieldsToCheck.forEach((field) => {
          const value = switchgear[field as keyof typeof switchgear];
          if (value == null || value === "") {
            delete switchgear[field as keyof typeof switchgear];
          }
        });
      });

      newHvSubstation.switchGears = switchgears;
      newHvSubstation.latitude = newHvSubstation.latitude || 0; //defaulting lat/long values to 0 if empty instead of making the field required - task #11760
      newHvSubstation.longitude = newHvSubstation.longitude || 0;
      newHvSubstation.company = companyName;
      newHvSubstation.site = siteName;
      newHvSubstation.accessNotes = newHvSubstation.accessNotes || "";

      const substationSubmission: HvSubstation2Submission = {
        ...newHvSubstation,
        customerId: customerId,
        name: newHvSubstation.name ?? "",
        surveyableAssets: switchgears,
      };

      createSubstation(substationSubmission);
    }
  });

  const {
    mutate: createSubstation,
    isError: substationCreationError,
    isLoading: substationCreationLoading,
    isSuccess: substationCreationSuccess,
    data: substationCreationResponse,
  } = useHvSubstationCreation();

  const altRegionNames = useAlternativeRegionNames(customerId);
  const companies = useCompaniesByCustomerId(customerId);
  const [companyName, setCompanyName] = useState<string | undefined>(undefined);
  const [siteName, setSiteName] = useState<string | undefined>(undefined);
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (selectedTabIndex: number) => {
    setActiveTab(selectedTabIndex);
  };

  const companyId = useMemo(() => {
    if (companies && companyName) {
      return companies.data?.find((c) => c.name === companyName)?.id;
    }
  }, [companies, companyName]);

  useEffect(() => {
    if (substationCreationError) {
      setShowErrorToast(true);
    } else if (!substationCreationLoading && substationCreationResponse && substationCreationSuccess) {
      onHvSubstationCreated(substationCreationResponse, newHvSubstation.name || "");
    }
  }, [
    newHvSubstation.name,
    onHvSubstationCreated,
    substationCreationError,
    substationCreationLoading,
    substationCreationResponse,
    substationCreationSuccess,
  ]);

  function handleHide() {
    setNewHvSubstation({});
    props.onClose();
  }

  function handleInputChange(event: any) {
    const fieldName = event.target.name as keyof HvSubstation2Submission;
    const value = event.target.value;
    formValidation.clearErrors(fieldName);
    formValidation.setValue(fieldName, value, { shouldValidate: false });
    setNewHvSubstation({ ...newHvSubstation, [fieldName]: value });
  }

  function handleAddressInputChange(event: any) {
    const name = event.target.name as keyof Address;
    const value = event.target.value;

    setNewAddress({ [name]: value });
  }

  function handleSwitchgearDetailsChange(updatedSwitchgear: SurveyableAsset) {
    formValidation.clearErrors();
    setSwitchgears((prevSwitchgears) =>
      prevSwitchgears.map((switchgear) =>
        switchgear.name === updatedSwitchgear.name ? updatedSwitchgear : switchgear,
      ),
    );
  }

  function coordinateInputTemplateWithValidation(key: keyof HvSubstation, minValue: number, maxValue: number) {
    if (key === "latitude" || key === "longitude") {
      return (
        <div>
          <div>
            <InputText
              type="number"
              key={`input-${key}`}
              value={newHvSubstation[key]}
              className={formValidation.formState.errors[key] ? "p-invalid" : undefined}
              id={key}
              {...formValidation.register(key, {
                max: maxValue,
                min: minValue,
              })}
              onChange={handleInputChange}
              name={key}
            />
          </div>
          <div>
            {formValidation.formState.errors[key] &&
              (formValidation.formState.errors[key]?.type === "max" ||
                formValidation.formState.errors[key]?.type === "min") && (
                <ValidationMessage
                  invalidMessage={t("common.minMaxNumberValidationMessage", {
                    min: minValue,
                    max: maxValue,
                  })}
                  validationType="error"
                  showIcon
                />
              )}
          </div>
        </div>
      );
    }
  }

  function accessNotesInputTemplate(key: keyof HvSubstation) {
    return (
      <div>
        <div>
          <InputTextarea
            key={`input-${key}`}
            value={newHvSubstation[key] as string}
            className={formValidation.formState.errors[key] ? "p-invalid" : undefined}
            id={key}
            {...formValidation.register(key, { maxLength: 500 })}
            onChange={handleInputChange}
            name={key}
          />
        </div>
        <div>
          {formValidation.formState.errors[key] && formValidation.formState.errors[key]?.type === "maxLength" && (
            <ValidationMessage
              invalidMessage={t("common.maxLengthValidationMessage", {
                maxLength: 500,
              })}
              validationType="error"
              showIcon
            />
          )}
        </div>
      </div>
    );
  }

  function addressInputTemplate(key: keyof Address, maxLength?: number, placeholder?: string) {
    const showAddressNameError = key === "name" && isAddressNameMissing && formValidation.formState.isSubmitted;

    return (
      <div>
        <span className="p-float-label w-100">
          <InputText
            key={`input-${key}`}
            name={key}
            value={newAddress[key]?.toString() || ""}
            onChange={handleAddressInputChange}
            maxLength={maxLength}
            className={showAddressNameError ? "p-invalid" : undefined}
          />
          <label htmlFor="inputtext">{placeholder}</label>
        </span>
        {showAddressNameError && (
          <ValidationMessage
            invalidMessage={t("common.pleaseEnter", {
              val: t("common.name", { entity: t("address.address") }),
            })}
            validationType="error"
            showIcon
          />
        )}
      </div>
    );
  }

  function handleCompanyChange(companyName: string | undefined) {
    setCompanyName(companyName);
    setSiteName(undefined);

    // If the user does not have admin or customer level permissions but do have company or
    // site level permissions, add validation to the company and site drop downs to ensure
    // they select a value, as they can only create resources at company/site level.
    formValidation.setValue("company", companyName ?? "", {
      shouldValidate: !userCanAddHvSubstationAtCustomerLevel,
    });

    formValidation.setValue("site", undefined, {
      shouldValidate: !userCanAddHvSubstationAtCustomerLevel,
    });

    if (!userCanAddHvSubstationAtCustomerLevel) {
      formValidation.clearErrors(["company", "site"]);
    }
  }

  function handleSiteChange(siteName: string | undefined) {
    setSiteName(siteName);

    // If the user does not have admin or customer level permissions but do have company
    // or site level permissions, add validation to the site drop down to ensure they
    // select a value, as they can only create resources at company/site level.
    formValidation.setValue("site", siteName ?? "", {
      shouldValidate: !userCanAddHvSubstationAtCustomerLevel,
    });

    if (!userCanAddHvSubstationAtCustomerLevel) {
      formValidation.clearErrors("site");
    }
  }

  const substationAddress = (
    <Stack spacing={1}>
      {addressInputTemplate("name", 100, t("common.name", { entity: "$t(address.address)" }))}
      {addressInputTemplate("line1", 100, t("address.line1"))}
      {addressInputTemplate("line2", 100, t("address.line2"))}
      {addressInputTemplate("line3", 100, t("address.line3"))}
      {addressInputTemplate("line4", 100, t("address.line4"))}
      {addressInputTemplate("locality", 100, t("address.locality"))}
      {addressInputTemplate("region", 100, t("address.region"))}
      {addressInputTemplate("postCode", 50, t("address.postCode"))}
      {addressInputTemplate("country", 100, t("address.country"))}
    </Stack>
  );

  const hvSubstationDetailsList: FormItem[] = [
    {
      label: t("substations.substationName"),
      required: true,
      value: (
        <div>
          <div>
            <InputText
              value={newHvSubstation.name?.toString() || ""}
              maxLength={50}
              className={formValidation.formState.errors.name ? "p-invalid" : undefined}
              id={"name"}
              {...formValidation.register("name", {
                required: t<string>("common.pleaseEnter", {
                  val: "$t(substations.substationName)",
                }),
                value: newHvSubstation.name,
              })}
              onChange={handleInputChange}
              name={"name"}
            />
          </div>
          <div>
            {formValidation.formState.errors?.name?.message && (
              <ValidationMessage
                invalidMessage={formValidation.formState.errors?.name?.message}
                validationType="error"
                showIcon
              />
            )}
          </div>
        </div>
      ),
    },
    {
      label: t("substations.substationNumber"),
      required: true,
      value: (
        <div>
          <div>
            <InputText
              value={newHvSubstation.code?.toString() || ""}
              maxLength={50}
              className={formValidation.formState.errors.code ? "p-invalid" : undefined}
              id={"code"}
              {...formValidation.register("code", {
                required: t<string>("common.pleaseEnter", {
                  val: "$t(substations.substationNumber)",
                }),
                value: newHvSubstation.code,
                validate: {
                  notDuplicate: (value) => !props.existingSubstations?.some((substation) => substation.code === value),
                },
              })}
              onChange={handleInputChange}
              name={"code"}
            />
          </div>
          <div>
            {formValidation.formState.errors?.code?.message && (
              <ValidationMessage
                invalidMessage={formValidation.formState.errors?.code?.message}
                validationType="error"
                showIcon
              />
            )}
            {formValidation.formState.errors?.code?.type === "notDuplicate" && (
              <ValidationMessage
                invalidMessage={t("substations.substationNumberDuplicationError")}
                validationType="error"
                showIcon
              />
            )}
          </div>
        </div>
      ),
    },
    { label: t("address.address"), value: substationAddress },
    {
      label: t("common.latitude"),
      value: coordinateInputTemplateWithValidation("latitude", -90, 90),
    },
    {
      label: t("common.longitude"),
      value: coordinateInputTemplateWithValidation("longitude", -180, 180),
    },
    {
      label: altRegionNames.data?.companyAltName ?? t("common.company"),
      // If the user does not have admin or customer level permissions but do have company or site level
      // permissions, make the company required and add validation to the drop down to ensure
      // they select a value, as they can only create resources at company/site level.
      required: !userCanAddHvSubstationAtCustomerLevel,
      value: (
        <div>
          <div>
            <Controller
              control={formValidation.control}
              name="company"
              defaultValue={companyName}
              rules={
                !userCanAddHvSubstationAtCustomerLevel
                  ? {
                      required: t<string>("common.pleaseSelect", {
                        val: "$t(common.company)",
                      }),
                      validate: {
                        notEmpty: (value) =>
                          !!value ||
                          t<string>("common.pleaseSelect", {
                            val: "$t(common.company)",
                          }),
                      },
                    }
                  : undefined
              }
              render={() => (
                <CompanyDropdown
                  customerId={customerId}
                  selectedCompanyName={companyName}
                  onCompanyChange={handleCompanyChange}
                  showClear
                />
              )}
            />
          </div>
          <div>
            {!userCanAddHvSubstationAtCustomerLevel && formValidation.formState.errors?.company && (
              <ValidationMessage
                invalidMessage={
                  (formValidation.formState.errors.company.message as string) ??
                  t("common.pleaseSelect", {
                    val: "$t(common.company)",
                  })
                }
                validationType="error"
                showIcon
              />
            )}
          </div>
        </div>
      ),
    },
    {
      label: altRegionNames.data?.siteAltName ?? t("common.site"),
      // If the user does not have admin or customer level permissions but do have company or site level
      // permissions, make the site required and add validation to the drop down to ensure
      // they select a value, as they can only create resources at company/site level.
      required: !userCanAddHvSubstationAtCustomerLevel,
      value: companyName ? (
        <div>
          <div>
            <Controller
              control={formValidation.control}
              name="site"
              defaultValue={siteName}
              rules={
                !userCanAddHvSubstationAtCustomerLevel
                  ? {
                      required: t<string>("common.pleaseSelect", { val: "$t(common.site)" }),
                      validate: {
                        notEmpty: (value) => !!value || t<string>("common.pleaseSelect", { val: "$t(common.site)" }),
                      },
                    }
                  : undefined
              }
              render={() => (
                <SiteDropdown
                  companyId={companyId}
                  customerId={customerId}
                  selectedSiteName={siteName}
                  onSiteChange={handleSiteChange}
                />
              )}
            />
          </div>
          <div>
            {!userCanAddHvSubstationAtCustomerLevel && formValidation.formState.errors?.site && (
              <ValidationMessage
                invalidMessage={
                  (formValidation.formState.errors.site.message as string) ??
                  t("common.pleaseSelect", { val: "$t(common.site)" })
                }
                validationType="error"
                showIcon
              />
            )}
          </div>
        </div>
      ) : (
        <SiteDropdown
          companyId={companyId}
          customerId={customerId}
          selectedSiteName={siteName}
          onSiteChange={handleSiteChange}
        />
      ),
    },
    {
      label: t("substations.accessNotes"),
      value: accessNotesInputTemplate("accessNotes"),
    },
  ];

  function handleAddSwitchgearClicked() {
    setSwitchgearDialogVisible(true);
  }

  function addSwitchgear(newAssetName: string, newAssetType: string) {
    const surveyableAsset: SurveyableAsset = {
      name: newAssetName,
      assetType: newAssetType,
      panels: [],
    };
    setSwitchgears([...switchgears, surveyableAsset]);
    setActiveTab(switchgears.length);
  }

  const header = <Typography variant="h5" component="p">{`${t("substations.hvSubstation")} `}</Typography>;

  const footer = (
    <Stack direction="row" justifyContent="flex-end" spacing={1} my={0.5}>
      <ProgressButton onClick={onSubmit} disabled={substationCreationLoading} loading={substationCreationLoading}>
        {" "}
        {t("common.create")}{" "}
      </ProgressButton>
      <CancelButton onClick={handleHide} disabled={substationCreationLoading} className="m1-1" />
    </Stack>
  );

  return (
    <FormProvider {...formValidation}>
      <Dialog
        header={header}
        footer={footer}
        visible={props.open}
        onHide={handleHide}
        breakpoints={{ "1960px": "75vw", "1300px": "90vw" }}
        style={{ width: "60vw" }}
      >
        <Container fluid>
          <Row>
            <Col xl={6} className="px-0">
              <Card title={t("substations.substationDetails")} className="mb-3 ">
                <FormContainer dataList={hvSubstationDetailsList} />
              </Card>
            </Col>
            <Col lg={6}>
              <Card title={t("asset.assets")} className="mb-3">
                {switchgearDialogVisible && (
                  <SwitchgearCreateDialog
                    visible={true}
                    headerMessage={t("common.createEntity", {
                      entity: "$t(asset.asset)",
                    })}
                    placeholder={t("common.name", {
                      entity: "$t(asset.asset)",
                    })}
                    maxTextLength={50}
                    onConfirm={addSwitchgear}
                    onClose={() => setSwitchgearDialogVisible(false)}
                    existingSubstationSwitchgearNames={switchgears.map((switchgear) => switchgear.name)}
                  />
                )}

                <div className="mt-2">
                  <TabView
                    activeTabIndex={activeTab}
                    onTabChange={handleTabChange}
                    tabLabels={switchgears.map((switchgear, index) => (
                      <SwitchgearLabel
                        switchgear={switchgear}
                        editing={false}
                        onChange={() => {}}
                        isSelected={activeTab === index}
                      />
                    ))}
                    addButtonEnabed={true}
                    onAddButtonClicked={handleAddSwitchgearClicked}
                  >
                    {switchgears.map((switchgear) => (
                      <SwitchgearDetails
                        key={switchgear.name}
                        switchgear={switchgear}
                        editable
                        onEdit={handleSwitchgearDetailsChange}
                      />
                    ))}
                  </TabView>
                </div>
              </Card>
              <div>
                {showErrorToast && (
                  <MessageToast
                    text={t("common.dataCreateError", {
                      val: "$t(substations.hvSubstation)",
                    })}
                    mode={ToastMode.Error}
                    show={showErrorToast}
                    autoHide
                  ></MessageToast>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </Dialog>
    </FormProvider>
  );
}
